// frontend/src/features/agency/Dashboard.js
// This React page renders the Agency dashboard: loads agencies, hydrates driver data, shows per-agency counters, and provides a sidebar to navigate Active, Reserved, and Banned driver views.
import React, { useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAgencies, selectAgenciesLoading } from '../agencies/agenciesSlice';
import { LayoutList, Slash, UserCheck, Menu, X } from 'lucide-react';

// NEW: per-agency counters (Total/Active/Reserved/Banned + Active limit)
import AgencyDriverStats from '../../components/AgencyDriverStats';
import { selectAgencyIdForUser } from '../auth/authSlice';
import { upsertDrivers } from '../drivers/driverSlice';
import { fetchDrivers } from '../../services/driverService';

export default function AgencyDashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const dispatch = useDispatch();
  const loading  = useSelector(selectAgenciesLoading);
  const agencyId = useSelector(selectAgencyIdForUser);

  // 1) Fire off the load once on mount:
  useEffect(() => {
    dispatch(fetchAgencies());
  }, [dispatch]);

   // Hydrate drivers for this agency so counters aren't zero on first paint
  useEffect(() => {
   if (!agencyId) return;
   (async () => {
     try {
       const [active, reserved, banned] = await Promise.all([
         fetchDrivers({ status: 'Active' }),
         fetchDrivers({ status: 'Reserved' }),
         fetchDrivers({ status: 'Banned' }),
       ]);
       dispatch(upsertDrivers([...active, ...reserved, ...banned]));
     } catch (e) {
       console.error('Agency hydrate failed', e);
     }
   })();
  }, [agencyId, dispatch]);

  // 2) Don’t render any child route until agencies have loaded:
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading agency data…
      </div>
    );
  }

  const btnBase = 'block w-full text-left px-4 py-2 rounded transition-all';

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-64'} bg-white border-r shadow-sm flex flex-col transition-all duration-200`}>
        <div className="p-4 flex items-center justify-between">
          {!collapsed && <h2 className="text-xl font-bold">Agency</h2>}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-gray-200"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <Menu size={20} /> : <X size={20} />}
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-1">
          <NavLink
            to="/agency/drivers"
            end
            className={({ isActive }) =>
              `${btnBase} ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} flex items-center`
            }
          >
            <LayoutList className="mr-2" />
            {!collapsed && 'Active Drivers'}
          </NavLink>
          <NavLink
            to="/agency/reserved"
            className={({ isActive }) =>
              `${btnBase} ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} flex items-center`
            }
          >
            <UserCheck className="mr-2" />
            {!collapsed && 'Reserved Drivers'}
          </NavLink>
          <NavLink
            to="/agency/banned"
            className={({ isActive }) =>
              `${btnBase} ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} flex items-center`
            }
          >
            <Slash className="mr-2" />
            {!collapsed && 'Banned Drivers'}
          </NavLink>
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-6 overflow-y-auto">
        {/* NEW: show per-agency counters at the top */}
        <AgencyDriverStats />
        <Outlet />
      </main>
    </div>
  );
}
